import { useState, useEffect } from "react";
import { jwtFetch } from "./utils/apiClient";
import { useParams, useNavigate } from "react-router-dom";
import { format, addMonths, addYears, subDays, differenceInCalendarMonths } from "date-fns";
import { config, priceFormat, numberFormat, shortenUrl } from "./Components/Config";
import Menu from "./Components/Menu";
import Header from "./Components/Header";

const generateName = (quotation) => {
  const company = quotation && quotation.company_name ? quotation.company_name.trim() : 'Company';

  const parseDate = (val) => {
    if (!val) return null;
    if (val instanceof Date) return isNaN(val) ? null : val;
    const n = Number(val);
    if (!isNaN(n)) return new Date(n);
    const d = new Date(val);
    return isNaN(d) ? null : d;
  };

  const start = parseDate(quotation && quotation.iou_start_date);
  const end = parseDate(quotation && quotation.iou_end_date);

  if (!start || !end || end < start) return `${company}-Contract`;

  const startLabel = format(start, 'MMM yyyy');
  const endLabel = format(end, 'MMM yyyy');
  const startMonth = format(start, 'MMM');
  const endMonth = format(end, 'MMM');
  const startYear = format(start, 'yyyy');
  const endYear = format(end, 'yyyy');

  if (startLabel === endLabel) {
    return `${company}-Contract-${startLabel}`;
  }

  if (startMonth === endMonth) {
    return `${company}-Contract-${startMonth} ${startYear}-${endYear}`;
  }

  return `${company}-Contract-${startLabel}-${endLabel}`;
};

const QuotationContract = () => {
  const { id_quotation } = useParams();
  const [ quotation, setQuotation ] = useState(null);
  const [ paymentSimulations, setPaymentSimulations ] = useState(null);
  const [ iouList, setIouList ] = useState(null);
  const [ contractName, setContractName ] = useState('');
  const [ contractSchedules, setContractSchedules ] = useState([]);
  const [ contractItems, setContractItems ] = useState([]);
  const [ contractAttachments, setContractAttachments ] = useState([]);
  const [ contractRemarks, setContractRemarks ] = useState('');
  const [ isLoading, setIsLoading ] = useState(false);

  const navigate = useNavigate();

  const crmUser = JSON.parse(localStorage.getItem('crmUser'));

  useEffect(() => {
    if (id_quotation) {
      getQuotation();
    }
  }, [id_quotation]);

  useEffect(() => {
    if (quotation) {
      if (quotation.iou_term_count) {
        getIou();
      } else {
        getPaymentSimulations();
      }
    }
  }, [quotation]);

  const getQuotation = async () => {
    try {
      const fetchQuotation = await jwtFetch(config.ENDPOINT + 'get/quotation', {
        method: 'POST',
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          id_quotation: id_quotation
        })
      });
      const responseGetQuotation = await fetchQuotation.json();
      if (responseGetQuotation.status === 200) {
        setQuotation(responseGetQuotation.data[0]);

        const name = generateName(responseGetQuotation.data[0]);
        setContractName(name);

        if (responseGetQuotation.data[0].attachment_files) {
          setContractAttachments(JSON.parse(responseGetQuotation.data[0].attachment_files));
        }
        const newItems = responseGetQuotation.data[0].quotation_item_list.map(item => ({
          quotation_item_list_id: item.id_quotation_item_list,
          product_id: item.product_id,
          product_display_name: item.product_display_name,
          product_type: item.product_type,
          product_currency_original: item.product_currency_original,
          product_currency: item.product_currency,
          product_cost_original: item.product_cost_original,
          product_cost: item.product_cost,
          product_price_original: item.product_price_original,
          product_price: item.product_price
        }));
        setContractItems([...contractItems, ...newItems]);
      } else {
        alert(responseGetQuotation.message);
      }
    } catch (error) {
      console.error('Error in getQuotation:', error);
      alert('An error occurred: ' + (error.message || error));
    }
  };

  const getPaymentSimulations = async () => {
    try {
      const fetchPaymentSimulations = await jwtFetch(config.ENDPOINT + 'get/quotation_payment_simulation', {
        method: 'POST',
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          quotation_id: id_quotation
        })
      });
      const responsePaymentSimulations = await fetchPaymentSimulations.json();
      if (responsePaymentSimulations.status === 200) {
        const newSchedules = responsePaymentSimulations.data.map(item => ({
          schedule_start_date: format(new Date(item.start_term_period), 'yyyy-MM-dd'),
          schedule_end_date: format(new Date(item.end_term_period), 'yyyy-MM-dd'),
          schedule_long_period: item.number_of_months,
          schedule_status: "Active",
          error_rate: true,
          error_date: false
        }));
        setContractSchedules([...contractSchedules, ...newSchedules]);
        setPaymentSimulations(responsePaymentSimulations.data);
      } else {
        alert(responsePaymentSimulations.message);
      }
    } catch (error) {
      console.error('Error in getPaymentSimulations:', error);
      alert('An error occurred: ' + (error.message || error));
    }
  };

  const getIou = async () => {
    try {
      const fetchIou = await jwtFetch(config.ENDPOINT + 'get/iou', {
        method: 'POST',
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          quotation_id: id_quotation
        })
      });
      const responseIou = await fetchIou.json();
      if (responseIou.status === 200) {
        const newSchedules = responseIou.data.map(item => ({
          schedule_start_date: format(new Date(item.start_term_period), 'yyyy-MM-dd'),
          schedule_end_date: format(new Date(item.end_term_period), 'yyyy-MM-dd'),
          schedule_long_period: item.number_of_months,
          schedule_status: "Active",
          error_rate: true,
          error_date: false
        }));
        setContractSchedules([...contractSchedules, ...newSchedules]);
        setIouList(responseIou.data);
      } else {
        alert(responseIou.message);
      }
    } catch (error) {
      console.error('Error in getIou:', error);
      alert('An error occurred: ' + (error.message || error));
    }
  };
  
  const addItem = () => {
    const item = {
      quotation_item_list_id: null,
      product_id: null,
      product_display_name: '',
      product_type: '',
      product_currency_original: 'IDR',
      product_currency: 'IDR',
      product_cost_original: 0,
      product_cost: 0,
      product_price_original: 0,
      product_price: 0
    }
    setContractItems([...contractItems, item]);
  }

  const changeItemData = (column, value, index) => {
    const item = contractItems[index];
    item[column] = value;
    setContractItems([...contractItems]);
  }

  const removeItem = (index) => {
    contractItems.splice(index, 1);
    setContractItems([...contractItems]);
  }
  
  const addSchedule = () => {
    const schedule = {
      schedule_start_date: contractSchedules ? format(new Date(), 'yyyy-MM-dd') : format(new Date(), 'yyyy-MM-dd'),
      schedule_end_date: null,
      schedule_long_period: 1,
      schedule_status: "Active",
      error_rate: true,
      error_date: true
    }
    setContractSchedules([...contractSchedules, schedule]);
  }

  const changeScheduleData = (column, value, index) => {
    const schedule = contractSchedules[index];
    schedule[column] = value;
    if (schedule.schedule_start_date && schedule.schedule_end_date) {
      try {
        const start = new Date(`${schedule.schedule_start_date}T00:00:00`);
        const end = new Date(`${schedule.schedule_end_date}T00:00:00`);
        const base = differenceInCalendarMonths(end, start);
        let months = base < 0 ? 0 : base;

        // A month period is considered reached when end date is at least
        // one day before the same date in the next month.
        if (months > 0) {
          const minimumEndForCurrentMonths = subDays(addMonths(start, months), 1);
          if (end < minimumEndForCurrentMonths) {
            months -= 1;
          }
        }

        schedule.schedule_long_period = months < 0 ? 0 : months;
        schedule.error_date = end < start;
      } catch (e) {
        schedule.schedule_long_period = 0;
        schedule.error_date = true;
      }
    }
    setContractSchedules([...contractSchedules]);
  }

  const removeSchedule = (index) => {
    contractSchedules.splice(index, 1);
    setContractSchedules([...contractSchedules]);
  }

  const addAttachments = () => {
    const attachment = {
      name: '',
      files: '',
      loading: false
    };
    setContractAttachments([...contractAttachments, attachment]);
  }

  const changeAttachmentData = (column, value, index) => {
    const attachment = contractAttachments[index];
    attachment[column] = value;
    if (column === 'files') {
      attachment.loading = false;
    }
    setContractAttachments([...contractAttachments]);
  }

  const uploadAttachments = async (files, index) => {
    changeAttachmentData('loading', true, index);
    const formData = new FormData();
    files.forEach((attachment) => {
      formData.append('files', attachment);
    });
    try {      
      const responseUploadAttachments = await jwtFetch(config.ENDPOINT + 'upload/gdrive', {
        method: 'POST',
        body: formData,
      });
      if (!responseUploadAttachments.ok) {
        throw new Error(`Upload failed with status: ${responseUploadAttachments.status}`);
      }
      const resultUploadAttachments = await responseUploadAttachments.json();
      if (resultUploadAttachments.status === 200) {
        changeAttachmentData('files', resultUploadAttachments.data, index);
      } else {
        alert(`Upload error: ${resultUploadAttachments.message}`);
      }
    } catch (error) {
      alert(`Upload error: ${error.message || error}`);
    } finally {
      changeAttachmentData('loading', false, index);
    }
  }

  const removeAttachments = (index) => {
    contractAttachments.splice(index, 1);
    setContractAttachments([...contractAttachments]);
  }

  const submitContractAdd = async (e) => {
    try {
      e.preventDefault();
      setIsLoading(true);
      const requestCOntractAdd = {
        company_id: quotation.company_id,
        subcompany_id: quotation.subcompany_id,
        quotation_id: id_quotation,
        contract_name: contractName,
        contract_items: contractItems.length > 0 ? JSON.stringify(contractItems) : null,
        contract_schedules: contractSchedules.length > 0 ? JSON.stringify(contractSchedules) : null,
        contract_attachments: contractAttachments.length > 0 ? JSON.stringify(contractAttachments) : null,
        contract_remarks: contractRemarks,
        contract_status: 'Active',
        created_on: format(new Date(), 'yyyy-MM-dd HH:mm:ss'),
        created_by: crmUser.user_keyid,
      };
      const fetchCOntractAdd = await jwtFetch(config.ENDPOINT + 'create/contract', {
        method: 'POST',
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(requestCOntractAdd)
      });
      const responseCOntractAdd = await fetchCOntractAdd.json();
      if (responseCOntractAdd.status === 200) {
        window.location.href = `/client/detail/${quotation.company_id}`;
      } else {
        alert(responseCOntractAdd.message);
      }
    } catch (error) {
      console.error('Error in submitContractAdd:', error);
      alert('An error occurred: ' + (error.message || error));
      setIsLoading(false);
    }
  }

  return (
    <div id="contract-add">
      <div className="container-fluid p-0">
        <div className="row g-0 mh-100vh">
          <div className="col-md-2 border-end">
            <div className="d-md-flex d-none align-items-center justify-content-center p-1 h-48">
              <a className="h-100" href="/dashboard"><img className="h-100" src="/cuancuan.png" alt="PointStar"/></a>
            </div>
            <hr className="m-0"/>
            <Menu active="quotation"/>
          </div>
          <div className="col-md-10">
            <Header/>
            <hr className="m-0"/>
            <div className="main">
              <div className="p-4">
                {quotation && (
                  <section className="mb-4">
                    <div className="d-flex align-items-center justify-content-between mb-3">
                      <div className="d-flex align-items-center">
                        <div className="fs-24"><button className="btn btn-link" onClick={() => navigate(-1)}><i className="bi bi-arrow-left"/></button> Contract Add</div>
                      </div>
                    </div>
                    <div className="row">
                      <div className="col-md-6">
                        <div className="row mb-2">
                          <div className="col-md-4 text-secondary">Quotation Number</div>
                          <div className="col-md-8">{quotation.quotation_code}</div>
                        </div>
                        <div className="row mb-2">
                          <div className="col-md-4 text-secondary">Company Group</div>
                          <div className="col-md-8">{quotation.company_name} <a href={`/client/detail/${quotation.company_id}`}><i className="bi bi-link-45deg"/></a></div>
                        </div>
                        <div className="row mb-2">
                          <div className="col-md-4 text-secondary">Subsidiary</div>
                          <div className="col-md-8">{quotation.subcompany_name} <a href={`/client/subsidiary/detail/${quotation.subcompany_id}`}><i className="bi bi-link-45deg"/></a></div>
                        </div>
                      </div>
                    </div>
                  </section>
                )}               

                <section className="mb-4">
                  <form onSubmit={(e) => submitContractAdd(e)}>
                    <div className="mt-4 mb-2">
                      <div className="d-flex align-items-center justify-content-between mb-3">
                        <div className="fs-24">Details</div>
                      </div>
                      <div className="row align-items-baseline form-group mb-2">
                        <div className="col-md-2">
                          <label>Contract Name <span className="text-danger">*</span></label>
                        </div>
                        <div className="col-md-5">
                          <input type="text" className="form-control" value={contractName} onChange={(e) => setContractName(e.target.value)} />
                        </div>
                      </div>
                      <div className="row align-items-baseline form-group mb-4">
                        <div className="col-md-2">
                          <label>Items <span className="text-danger">*</span></label>
                        </div>
                        <div className="col-md-10">
                          <div className="table-responsive">
                            <div className="table-responsive">
                              <table className="table table-bordered mb-2">
                                <thead>
                                  <tr>
                                    <th className="bg-secondary text-light" style={{textAlign: 'center'}}>#</th>
                                    <th className="bg-secondary text-light">SKUs</th>
                                    <th className="bg-secondary text-light" style={{textAlign: 'center'}}>Type</th>
                                    <th className="bg-secondary text-light" style={{textAlign: 'right'}}>Cost Price</th>
                                    <th className="bg-secondary text-light" style={{textAlign: 'right'}}>Selling Price</th>
                                    <th className="bg-secondary text-light"></th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {contractItems && contractItems.map((item, s) =>
                                    <tr key={"item-" + s}>
                                      <td align="center" valign="middle">{s + 1}</td>
                                      <td align="left" valign="middle">{item.product_display_name}</td>
                                      <td align="left" valign="middle">{item.product_type}</td>
                                      <td align="right" valign="middle">
                                        {priceFormat(item.product_cost_original, item.product_currency_original)}
                                      </td>
                                      <td align="right" valign="middle">
                                        {priceFormat(item.product_price_original, item.product_currency_original)}
                                      </td>
                                      <td align="center">
                                        <button className="btn btn-sm btn-link text-danger" type="button" onClick={() => removeItem(s)} disabled={contractItems.length === 1}><i className="bi bi-trash"/></button>
                                      </td>
                                    </tr>
                                  )}
                                </tbody>
                              </table>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="row align-items-baseline form-group mb-4">
                        <div className="col-md-2">
                          <label>Payment Schedules <span className="text-danger">*</span></label>
                        </div>
                        <div className="col-md-10">
                          <div className="table-responsive">
                            <div className="table-responsive">
                              <table className="table table-bordered mb-2">
                                <thead>
                                  <tr>
                                    <th className="bg-secondary text-light">Schedules</th>
                                    <th className="bg-secondary text-light" style={{textAlign: 'center'}}>Period Start Date</th>
                                    <th className="bg-secondary text-light" style={{textAlign: 'center'}}>Period End Date</th>
                                    <th className="bg-secondary text-light w-80" style={{textAlign: 'center'}}>Months</th>
                                    <th className="bg-secondary text-light"></th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {contractSchedules && contractSchedules.map((schedule, s) =>
                                    <tr key={"schedule-" + s}>
                                      <td valign="middle">Schedule {s + 1}</td>
                                      <td><input className={"form-control " + (schedule.error_date ? 'border-danger' : '')} type="date" value={schedule.schedule_start_date} onChange={(e) => changeScheduleData('schedule_start_date', e.target.value, s)} required/></td>
                                      <td><input className={"form-control " + (schedule.error_date ? 'border-danger' : '')} type="date" value={schedule.schedule_end_date} onChange={(e) => changeScheduleData('schedule_end_date', e.target.value, s)} required/></td>
                                      <td align="center" valign="middle">
                                        {schedule.schedule_long_period}
                                        {/* <input className="form-control" type="number" value={schedule.schedule_long_period} onChange={(e) => changeScheduleData('schedule_long_period', e.target.value, s)} required/> */}
                                      </td>
                                      <td align="center">
                                        <button className="btn btn-sm btn-link text-danger" type="button" onClick={() => removeSchedule(s)} disabled={contractSchedules.length === 1}><i className="bi bi-trash"/></button>
                                      </td>
                                    </tr>
                                  )}
                                </tbody>
                              </table>
                            </div>
                          </div>
                          <div className="d-flex justify-content-start">
                            <button className="btn btn-sm btn-outline-secondary" type="button" onClick={() => addSchedule()}><i className="bi bi-plus-lg me-2"/> Schedule</button>
                          </div>
                        </div>
                      </div>
                      <div className="row align-items-baseline form-group mb-4">
                        <div className="col-md-2">
                          <label>Attachments</label>
                        </div>
                        <div className="col-md-10">
                          <div className="table-responsive">
                            <table className="table table-bordered mb-2">
                              <thead>
                                <tr>
                                  <th className="bg-secondary text-light" style={{textAlign: 'center'}}>#</th>
                                  <th className="bg-secondary text-light w-400">Name</th>
                                  <th className="bg-secondary text-light">File</th>
                                  <th className="bg-secondary text-light"></th>
                                </tr>
                              </thead>
                              <tbody>
                                {contractAttachments && contractAttachments.map((attachment, a) =>
                                  <tr key={"attachment-" + a}>
                                    <td valign="middle" align="center">{a + 1}</td>
                                    <td valign="middle">
                                      <input className="form-control" type="text" value={attachment.name} onChange={(e) => changeAttachmentData('name', e.target.value, a)} required />
                                    </td>
                                    <td valign="middle">
                                      {attachment.loading === false ? (
                                        <div>
                                          {attachment.files ? (
                                            <a className="ellipsis" href={attachment.files[0].fileUrl} target="_blank" rel="noopener noreferrer">{shortenUrl(attachment.files[0].fileUrl, 50)} <i className="bi bi-link-45deg ms-2"/></a>
                                          ) : (
                                            <input className="form-control" type="file" accept=".pdf, .png, .jpg, .jpeg, .gif, .webp, .svg, .bmp, .avif, .csv, .xls, .xlsx" onChange={(e) => uploadAttachments(Array.from(e.target.files), a)} required />
                                          )}
                                        </div>
                                      ) : 'Loading...'}
                                    </td>
                                    <td valign="middle" align="center">
                                      <button className="btn btn-sm btn-link text-danger" type="button" onClick={() => removeAttachments(a)}><i className="bi bi-trash"/></button>
                                    </td>
                                  </tr>
                                )}
                              </tbody>
                            </table>
                          </div>
                          <div className="d-flex justify-content-start">
                            <button className="btn btn-sm btn-outline-secondary" type="button" onClick={() => addAttachments()}><i className="bi bi-plus-lg me-2"/> Attachment</button>
                          </div>
                        </div>
                      </div>
                      <div className="row align-items-baseline form-group mb-2">
                        <div className="col-md-2">
                          <label>Remarks</label>
                        </div>
                        <div className="col-md-10">
                          <textarea className="form-control" rows="4" value={contractRemarks} placeholder="Additional information for this Contract..." onChange={(e) =>setContractRemarks(e.target.value)}/>
                        </div>
                      </div>
                    </div>
                    <div className="mb-4">
                      <div className="d-flex justify-content-end">
                        <a className="btn btn-link" href={"/quotation/detail/" + id_quotation}>Cancel</a>
                        <button className="btn btn-primary ms-2" type="submit" disabled={isLoading}>{isLoading ? 'Loading...' : 'Save'}</button>
                      </div>
                    </div>
                  </form>
                </section>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default QuotationContract;


